import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, Checkbox, FormControlLabel } from '@mui/material';
import { ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import { withTranslation } from 'react-i18next';
import SparaButton from 'components/SparaButton/SparaButton';
import CustomInput from 'components/CustomInput/CustomInput';
import styles from './EventFormBuilder.module.scss';

const FIELD_TYPES = ['text', 'textarea', 'select', 'checkbox', 'phone'];

const emptyField = () => ({
    type: 'text',
    label: '',
    label_en: '',
    required: false,
    options: [],
});

const EventFormBuilder = ({ fields = [], onChange, hasResponses = false, t }) => {
    const update = (next) => onChange(next);

    const addField = () => update([...fields, emptyField()]);

    const changeField = (index, patch) => {
        update(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)));
    };

    const move = (index, delta) => {
        const nextIndex = index + delta;
        if (nextIndex < 0 || nextIndex >= fields.length) return;
        const next = [...fields];
        const [item] = next.splice(index, 1);
        next.splice(nextIndex, 0, item);
        update(next);
    };

    const remove = (index) => {
        update(fields.filter((_, i) => i !== index));
    };

    return (
        <div className={styles.builder}>
            <div className={styles.header}>
                <h4 className={styles.title}>{t('events.form_builder_title')}</h4>
                <SparaButton type="button" variant="outlined" size="small" onClick={addField}>
                    {t('events.add_field')}
                </SparaButton>
            </div>
            {fields.length === 0 ? (
                <p className={styles.empty}>{t('events.form_builder_empty')}</p>
            ) : (
                fields.map((field, index) => (
                    <div key={field.id || `new-${index}`} className={styles.fieldRow}>
                        <FormControl fullWidth>
                            <InputLabel shrink>{t('events.field_type')}</InputLabel>
                            <Select
                                value={field.type || 'text'}
                                disabled={hasResponses && !!field.id}
                                onChange={(e) => changeField(index, { type: e.target.value })}
                            >
                                {FIELD_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {t(`events.field_type_${type}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <CustomInput
                            labelText={t('events.field_label')}
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                value: field.label || '',
                                onChange: (e) => changeField(index, { label: e.target.value }),
                            }}
                        />
                        <CustomInput
                            labelText={t('events.field_label_en')}
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                value: field.label_en || '',
                                onChange: (e) => changeField(index, { label_en: e.target.value }),
                            }}
                        />
                        {field.type === 'select' && (
                            <CustomInput
                                labelText={t('events.field_options')}
                                formControlProps={{ fullWidth: true }}
                                inputProps={{
                                    value: Array.isArray(field.options)
                                        ? field.options
                                              .map((opt) =>
                                                  typeof opt === 'string' ? opt : opt.label || opt.value || ''
                                              )
                                              .join(', ')
                                        : '',
                                    onChange: (e) =>
                                        changeField(index, {
                                            options: e.target.value
                                                .split(',')
                                                .map((s) => s.trim())
                                                .filter(Boolean),
                                        }),
                                }}
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!field.required}
                                    onChange={(e) => changeField(index, { required: e.target.checked })}
                                />
                            }
                            label={t('events.field_required')}
                        />
                        <div className={styles.actions}>
                            <button type="button" className={styles.iconButton} onClick={() => move(index, -1)}>
                                <ArrowUpward fontSize="small" />
                            </button>
                            <button type="button" className={styles.iconButton} onClick={() => move(index, 1)}>
                                <ArrowDownward fontSize="small" />
                            </button>
                            <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() => remove(index)}
                            >
                                <Delete fontSize="small" />
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default withTranslation()(EventFormBuilder);
